<template>
  <div class="modal-component" @click="handleOverlayClick">
    <div class="modal-newsletter" @click.stop>
      <div class="modal-newsletter__header">
        <button class="modal-newsletter__close-button" @click="closeModal"><icon-close style="--fill-color: #FFF;" /></button>
      </div>
      <div class="modal-newsletter__body">
        <div v-if="! signupMessage" class="newsletter__container">
          <div class="newsletter__title">The A Sound Effect Newsletter</div>
          <div class="newsletter__tagline">Want free sound effects?</div>
          <p class="newsletter__details">
            Get the much-loved A Sound Effect newsletter,
            exclusive sound stories - and free sound effects
            with <span class="newsletter__highlight-text">every</span> issue!
          </p>
          <p class="newsletter__details">
            Plus, you get 800+ sounds as a welcome gift when you sign up 🎉
          </p>
          <form class="newsletter__form" @submit.prevent="newsletterSignup">
            <div class="newsletter__input_group">
              <input
                class="newsletter__input"
                type="text"
                id="name"
                autocomplete="name"
                placeholder="Your name"
                v-model="name"
                required />
              <input
                class="newsletter__input"
                type="text"
                id="email"
                autocomplete="email"
                placeholder="Your email address"
                v-model="email"
                required />
            </div>
            <div class="newsletter__checkbox-group">
              <input
                class="newsletter__input-checkbox"
                type="checkbox"
                v-model="termsValue"
                id="terms"
                name="terms"
                required />
              <label class="check-box__label" for="terms">
                <span class="newsletter__checkbox-text">I agree to receive the A Sound Effect newsletter and accept the <a href="/privacy-policy/" target="__blank">privacy policy</a>. Unsubscribe anytime.</span>
              </label>
            </div>
            <button class="newsletter__submit-button">Get The Newsletter (And Free Sounds)</button>
          </form>
        </div>
        <div v-else class="newsletter__container">
          <div class="newsletter__title">The A Sound Effect Newsletter</div>
          <div class="newsletter__tagline">{{ signupMessage }}</div>
          <p class="newsletter__details">
            Keep an eye on your inbox for your welcome gift 🎁
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { newsletterSignupMutationHandler } from "~/hooks/mutations/newsletter-signup-mutation-handler";
import { getRecaptchaToken, loadRecaptcha, unmountRecaptcha } from "~/composables/grecaptcha";
import { useUserStore, defineAsyncComponent } from "#imports";
const IconClose = defineAsyncComponent(() => import('~/assets/icons/IconClose.svg'));

export default {
  props: {
    actionTrigger: {
      type: String,
      default: ''
    }
  },
  components: {
    IconClose,
  },
  setup() {
    return {
      userStore: useUserStore()
    }
  },
  data() {
    return{
      name: '',
      email: '',
      termsValue: false,
      signupMessage: '',
    }
  },
  mounted() {
    loadRecaptcha();
  },
  unmounted() {
    unmountRecaptcha()
    this.signupMessage = '';
  },
  methods: {
    closeModal() {
      this.$emit('close-modal');
    },
    handleOverlayClick() {
      this.closeModal();
    },
    async newsletterSignup() {
      if (!this.termsValue) {
        throw new Error('Terms must be agreed to subscribe.');
      }

      const token = await getRecaptchaToken();

      if (! token) {
        throw new Error('reCAPTCHA validation failed');
      }

      await newsletterSignupMutationHandler({
        name: this.name,
        email: this.email,
        trigger: this.actionTrigger,
        captchaToken: token,
      })

      this.signupMessage = 'Thank you for subscribing!';

      // set cookie to stop popup
      this.userStore.setHideNewsletterSignup(true)
    },
  }
}
</script>

<style lang="scss">
@use "~/common/scss/variables";
@use "~/common/scss/mq";

.modal-component {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  @include mq.mq(null, variables.$mobileContainerBreak) {
    align-items: start;
    overflow-y: scroll;
  }
  .modal-newsletter {
    background-color: #1E1E1E;
    border-radius: 10px;
    overflow: hidden;
    max-width: 90%;
    width: 840px;
    transition: all 0.3s ease;
    &--small {
      width: 100%;
    }
    &--medium {
      width: 605px;
    }
    // tablet
    @include mq.mq(null, 650) {
      max-width: 100%;
      width: 100%;
      border-radius: 0;
    }
    &--large {
      width: 840px;
    }
    &__close-button {
      color: #555;
      position: absolute;
      top: 0.6rem;
      right: 1rem;
      font-size: 1.6rem;
      cursor: pointer;
      padding-top: 2px;
    }
    &__header, &__footer {
      padding: 20px;
    }
    &__header {
      position: relative;
    }
    &__body {
      text-align: center;
      font-family: "DM Sans", sans-serif;
      display: flex;
      align-items: start;
      justify-content: center;
      padding: 20px 100px 70px 100px;
      @include mq.mq(null, 822) {
        padding: 20px 10% 70px 10%;
      }
      @include mq.mq(null, 390) {
        padding: 30px 20px 70px 20px;
      }

      .newsletter {
        &__container {
          display: flex;
          flex-direction: column;
          gap: 0.5em;
        }
        &__title {
          color: #DB7A0A;
          font-weight: 700;
          font-size: 12px;
          text-transform: uppercase;
        }
        &__tagline {
          color: #FFF;
          font-weight: 900;
          font-size: 44px;
          text-transform: uppercase;
          letter-spacing: 0%;
          line-height: 100%;
          margin-bottom: 6px;
          @include mq.mq(null, 822) {
            font-size: 28px;
          }
        }
        &__details {
          margin: auto;
          color: #FFF;
          max-width: 640px;
          font-weight: 500;
          font-size: 17px;
          line-height: 100%;
          padding-bottom: 16px;
        }
        &__highlight-text {
          color: #DB7A0A;
          font-style: italic;
        }
        &__input_group {
            display: flex;
            gap: 15px;
            margin-bottom: 20px;
            @include mq.mq(null, 390) {
              flex-direction: column;
              gap: 15px;
            }
        }
        &__input {
          width: 100%;
          background: #fff;
          color: #8C8C8C;
          font-size: 14px;
          border-radius: 2px;
          padding: 12px 10px;
        }
        &__checkbox-group {
          display: flex;
          margin-bottom: 20px;
        }
        &__input-checkbox {
          width: 20px;
          height: 20px;
        }
        &__checkbox-text {
          padding-left: 3px;
          color: #FFF;
          font-size: 13px;
          a {
            color: #FFF;
          }
        }
        &__submit-button {
          border-radius: 3px;
          height: 35px;
          font-size: 15px;
          padding: 0 20px;
          background-color: #DB7A0A;
          color: #FFF;

          @include mq.mq(null, 390) {
            font-size: 12px;
          }

          &:hover {
            opacity: 0.8;
          }
        }
      }
    }
    &__footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
    }
  }
}
</style>
